import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserAPI } from 'api/user';
import { RlMarketplaceApi } from 'marketplace/api/rl-marketplace-api';
export const MARKETPLACE_AUTH_TOKEN_QUERY_KEY = '__marketplace_auth_token';
export const useGetMarketplaceAuthToken = (local)=>{
    return useQuery({
        queryKey: [
            'marketplace-auth-token'
        ],
        queryFn: ()=>{
            if (local) {
                return Promise.resolve({
                    token: local
                });
            }
            if (localStorage.getItem(MARKETPLACE_AUTH_TOKEN_QUERY_KEY)) {
                return Promise.resolve({
                    token: localStorage.getItem(MARKETPLACE_AUTH_TOKEN_QUERY_KEY)
                });
            }
            return UserAPI.getMarketplaceAuthToken();
        },
        onSuccess: (data)=>{
            if (data.token) {
                localStorage.setItem(MARKETPLACE_AUTH_TOKEN_QUERY_KEY, data.token);
            }
        }
    });
};
export const useGetInstalledWidgets = (location, enabled)=>{
    return useQuery({
        queryKey: [
            'installed-widgets',
            location
        ],
        enabled: enabled !== null && enabled !== void 0 ? enabled : true,
        queryFn: async ()=>{
            var _apps_filter;
            const apps = await RlMarketplaceApi.getInstalledWidgets();
            // Disabled installations must not render widgets in product surfaces.
            // Some installed apps (e.g. integrations) omit `widgets` entirely —
            // treat missing as [] so one bad entry cannot fail the whole query.
            const filteredApps = apps === null || apps === void 0 ? void 0 : (_apps_filter = apps.filter((a)=>{
                var _a_installation;
                return !((_a_installation = a.installation) === null || _a_installation === void 0 ? void 0 : _a_installation.disabled);
            })) === null || _apps_filter === void 0 ? void 0 : _apps_filter.map((a)=>{
                var _a_widgets;
                return {
                    ...a,
                    widgets: ((_a_widgets = a.widgets) !== null && _a_widgets !== void 0 ? _a_widgets : []).filter((w)=>{
                        var _ref;
                        var _w_supportedLocations_locations, _w_supportedLocations;
                        return (_ref = (_w_supportedLocations = w.supportedLocations) === null || _w_supportedLocations === void 0 ? void 0 : (_w_supportedLocations_locations = _w_supportedLocations.locations) === null || _w_supportedLocations_locations === void 0 ? void 0 : _w_supportedLocations_locations.includes(location)) !== null && _ref !== void 0 ? _ref : false;
                    })
                };
            });
            return Promise.resolve(filteredApps);
        }
    });
};
export const useGetWidgetByIdFromMarketplace = (appId, versionId, widgetId)=>{
    return useQuery({
        queryKey: [
            'marketplace-widget',
            widgetId
        ],
        queryFn: ()=>RlMarketplaceApi.getWidgetById(appId, versionId, widgetId)
    });
};
const INSTALLED_MARKETPLACE_APPS_QUERY_KEY = [
    'installed-marketplace-apps'
];
export const useGetInstalledMarketplaceApps = (enabled)=>{
    return useQuery({
        queryKey: INSTALLED_MARKETPLACE_APPS_QUERY_KEY,
        queryFn: ()=>RlMarketplaceApi.getInstalledMarketplaceApps(),
        enabled: enabled !== null && enabled !== void 0 ? enabled : true
    });
};
const appPermissionQueryKey = (appId)=>[
        'app-permission',
        appId
    ];
export const useGetAppPermission = (appId, enabled = true)=>{
    return useQuery({
        queryKey: appPermissionQueryKey(appId),
        enabled: enabled && !!appId,
        queryFn: ()=>RlMarketplaceApi.getAppPermission(appId)
    });
};
export const useUpdateAppPermission = ()=>{
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ appId, readUserGroupIds })=>RlMarketplaceApi.updateAppPermission(appId, readUserGroupIds),
        onSuccess: (_data, { appId })=>{
            queryClient.invalidateQueries({
                queryKey: appPermissionQueryKey(appId)
            });
        }
    });
};
// Returns all installed widgets without client-side location filtering (for settings grid)
export const useGetAllInstalledWidgets = (enabled = true)=>{
    return useQuery({
        queryKey: [
            'installed-widgets-all'
        ],
        enabled,
        queryFn: ()=>RlMarketplaceApi.getInstalledWidgets()
    });
};
const appLocationPermissionsQueryKey = (appId)=>[
        'app-location-permissions',
        appId
    ];
export const useGetAppLocationPermissions = (appId, enabled = true)=>{
    return useQuery({
        queryKey: appLocationPermissionsQueryKey(appId),
        enabled: enabled && !!appId,
        queryFn: ()=>RlMarketplaceApi.getAppLocationPermissions(appId)
    });
};
export const useUpdateAppLocationPermissions = ()=>{
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ appId, entries })=>RlMarketplaceApi.updateAppLocationPermissions(appId, entries),
        onSuccess: (_data, { appId })=>{
            queryClient.invalidateQueries({
                queryKey: appLocationPermissionsQueryKey(appId)
            });
        }
    });
};
export const useUpdateMarketplaceAppStatus = ()=>{
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ appId, status })=>{
            if (status) {
                return RlMarketplaceApi.enableAppInstallation(appId);
            }
            return RlMarketplaceApi.disableAppInstallation(appId);
        },
        onSuccess: ()=>{
            queryClient.refetchQueries({
                queryKey: INSTALLED_MARKETPLACE_APPS_QUERY_KEY
            });
            queryClient.refetchQueries({
                queryKey: [
                    'installed-widgets-all'
                ]
            });
            queryClient.refetchQueries({
                queryKey: [
                    'installed-widgets'
                ]
            });
        }
    });
};
